export type RuntimeEnvironment = 'local' | 'dev' | 'test' | 'staging' | 'prod';
export type AuthMode = 'mock' | 'cognito';
export type ApiMode = 'mock' | 'real';

export interface DocumentUploadLimitsBytes {
  absolute: number;
  pdf: number;
  docx: number;
  xlsx: number;
  csv: number;
  txt: number;
  image: number;
}

export interface RuntimeConfig {
  environment: RuntimeEnvironment;
  apiBaseUrl: string;
  awsRegion: string;
  cognitoUserPoolId: string;
  cognitoClientId: string;
  authMode: AuthMode;
  apiMode: ApiMode;
  documentUploadLimitsBytes: DocumentUploadLimitsBytes;
}

let activeConfig: RuntimeConfig | null = null;

function assertString(value: unknown, key: string): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`Route42 runtime configuration value ${key} is required.`);
  }
  return value.trim();
}

function assertPositiveInteger(value: unknown, key: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0) {
    throw new Error(`Route42 runtime configuration value ${key} must be a positive integer.`);
  }
  return value;
}

function parseDocumentUploadLimits(value: unknown): DocumentUploadLimitsBytes {
  if (!value || typeof value !== 'object') {
    throw new Error('Route42 runtime configuration value documentUploadLimitsBytes is required.');
  }
  const raw = value as Record<string, unknown>;
  return {
    absolute: assertPositiveInteger(raw.absolute, 'documentUploadLimitsBytes.absolute'),
    pdf: assertPositiveInteger(raw.pdf, 'documentUploadLimitsBytes.pdf'),
    docx: assertPositiveInteger(raw.docx, 'documentUploadLimitsBytes.docx'),
    xlsx: assertPositiveInteger(raw.xlsx, 'documentUploadLimitsBytes.xlsx'),
    csv: assertPositiveInteger(raw.csv, 'documentUploadLimitsBytes.csv'),
    txt: assertPositiveInteger(raw.txt, 'documentUploadLimitsBytes.txt'),
    image: assertPositiveInteger(raw.image, 'documentUploadLimitsBytes.image'),
  };
}

function parseRuntimeConfig(value: unknown): RuntimeConfig {
  if (!value || typeof value !== 'object') {
    throw new Error('Route42 runtime configuration must be a JSON object.');
  }

  const raw = value as Record<string, unknown>;
  const environment = assertString(raw.environment, 'environment') as RuntimeEnvironment;
  const authMode = assertString(raw.authMode, 'authMode') as AuthMode;
  const apiMode = assertString(raw.apiMode, 'apiMode') as ApiMode;

  if (!['local', 'dev', 'test', 'staging', 'prod'].includes(environment)) {
    throw new Error(`Unsupported Route42 environment: ${environment}`);
  }
  if (!['mock', 'cognito'].includes(authMode)) {
    throw new Error(`Unsupported Route42 auth mode: ${authMode}`);
  }
  if (!['mock', 'real'].includes(apiMode)) {
    throw new Error(`Unsupported Route42 API mode: ${apiMode}`);
  }
  if (environment === 'prod' && (authMode !== 'cognito' || apiMode !== 'real')) {
    throw new Error('Route42 production runtime configuration cannot use mock auth or API mode.');
  }

  return {
    environment,
    apiBaseUrl: assertString(raw.apiBaseUrl, 'apiBaseUrl').replace(/\/$/, ''),
    awsRegion: assertString(raw.awsRegion, 'awsRegion'),
    cognitoUserPoolId: assertString(raw.cognitoUserPoolId, 'cognitoUserPoolId'),
    cognitoClientId: assertString(raw.cognitoClientId, 'cognitoClientId'),
    authMode,
    apiMode,
    documentUploadLimitsBytes: parseDocumentUploadLimits(raw.documentUploadLimitsBytes),
  };
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const response = await fetch('/config/runtime-config.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(
      'Route42 runtime configuration is missing. Generate apps/web/public/config/runtime-config.json before starting the web app.',
    );
  }
  return parseRuntimeConfig(await response.json());
}

export function setRuntimeConfig(config: RuntimeConfig): void {
  activeConfig = config;
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!activeConfig) {
    throw new Error('Route42 runtime configuration has not been initialized.');
  }
  return activeConfig;
}
